import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { User, UserModulePermissions, ModulePermissions } from '../../types/user';
import { useUsers } from '../../hooks/useUsers';
import { usePermissions } from '../../hooks/usePermissions';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { DEFAULT_PERMISSIONS, MODULE_LABELS, PERMISSION_COLORS, PERMISSION_LABELS } from '../../config/permissions';

interface UserPermissionsManagerProps {
  userId?: string;
  onClose?: () => void;
}

const UserPermissionsManager: React.FC<UserPermissionsManagerProps> = ({ userId, onClose }) => {
  const { users, loading: usersLoading, refresh } = useUsers();
  const { isSuperAdmin } = usePermissions();
  const { t } = useLanguage();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<UserModulePermissions | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userId) {
      const user = users.find(u => u.id === userId);
      if (user) {
        setSelectedUser(user);
        setPermissions(user.permissions || getDefaultPermissions(user.role));
      }
    }
  }, [userId, users]);

  const getDefaultPermissions = (role: string): UserModulePermissions => {
    return DEFAULT_PERMISSIONS[role] || DEFAULT_PERMISSIONS.user;
  };

  const handlePermissionChange = (module: keyof UserModulePermissions, permission: ModulePermissions) => {
    if (permissions) {
      setPermissions({ ...permissions, [module]: permission });
    }
  };

  const handleSave = async () => {
    if (!selectedUser || !permissions) return;

    setSaving(true);
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      await updateDoc(userRef, {
        permissions: permissions,
        updatedAt: new Date()
      });
      
      await refresh();
      toast.success(t('admin.permissions.saveSuccess'));
      onClose?.();
    } catch (error) {
      console.error('Error updating user permissions:', error);
      toast.error(t('admin.permissions.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setPermissions(user.permissions || getDefaultPermissions(user.role));
  };

  const getPermissionColor = (permission: ModulePermissions) => {
    return PERMISSION_COLORS[permission] || PERMISSION_COLORS.none;
  };

  if (!isSuperAdmin) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Nincs jogosultságod ehhez a funkcióhoz</p>
      </div>
    );
  }

  if (usersLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Felhasználók betöltése...</p>
      </div>
    );
  }

  const modules: Array<{ key: keyof UserModulePermissions; label: string }> = 
    Object.entries(MODULE_LABELS).map(([key, label]) => ({ 
      key: key as keyof UserModulePermissions, 
      label 
    }));

  const permissionOptions: Array<{ value: ModulePermissions; label: string }> = [
    { value: 'none', label: PERMISSION_LABELS.none },
    { value: 'access', label: PERMISSION_LABELS.access },
    { value: 'admin', label: PERMISSION_LABELS.admin }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">
          Felhasználói jogosultságok kezelése
        </h2>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {/* Felhasználó kiválasztása */}
      {!userId && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map(user => (
            <button
              key={user.id}
              onClick={() => handleUserSelect(user)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                selectedUser?.id === user.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-medium">
                  {user.displayName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{user.displayName}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                  <p className="text-xs text-gray-400">
                    {user.role === 'super_admin' ? 'Szuperadmin' : 
                     user.role === 'admin' ? 'Admin' : 'Felhasználó'}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Jogosultságok szerkesztése */}
      {selectedUser && permissions && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {selectedUser.displayName} jogosultságai
              </h3>
              <p className="text-sm text-gray-500">{selectedUser.email}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              selectedUser.role === 'super_admin' ? 'bg-red-100 text-red-800' :
              selectedUser.role === 'admin' ? 'bg-blue-100 text-blue-800' :
              'bg-green-100 text-green-800'
            }`}>
              {selectedUser.role === 'super_admin' ? 'Szuperadmin' : 
               selectedUser.role === 'admin' ? 'Admin' : 'Felhasználó'}
            </span>
          </div>

          <div className="space-y-4">
            {modules.map(module => (
              <div key={module.key} className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <span className="font-medium text-gray-900">{module.label}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                    getPermissionColor(permissions[module.key])
                  }`}>
                    {permissionOptions.find(p => p.value === permissions[module.key])?.label}
                  </span>
                </div>
                <select
                  value={permissions[module.key]}
                  onChange={(e) => handlePermissionChange(module.key, e.target.value as ModulePermissions)}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {permissionOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
                disabled={saving}
              >
                Mégse
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Mentés...' : 'Mentés'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPermissionsManager; 